import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import StudentModal, { type StudentEdit } from './components/StudentModal';
import StudentDetailModal, { type StudentDetail } from './components/StudentDetailModal';

interface StudentRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  level: string | null;
  group_id: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  register_date: string | null;
  last_login: string | null;
  performance: string | null;
  attendance: string | null;
  notes: string | null;
  group_name: string | null;
}

interface RelatedCounts {
  packages: number;
  payments: number;
  submissions: number;
}

type ActiveFilter = 'tumu' | 'aktif' | 'pasif';
type ToggleAction = 'deactivate' | 'activate';

export default function Ogrenciler() {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('tumu');
  const [modalOpen, setModalOpen] = useState(false);
  const [editStudent, setEditStudent] = useState<StudentRow | null>(null);
  const [detailStudent, setDetailStudent] = useState<StudentRow | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<{ student: StudentRow; action: ToggleAction } | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleteStudent, setDeleteStudent] = useState<StudentRow | null>(null);
  const [checkingRelated, setCheckingRelated] = useState(false);
  const [relatedCounts, setRelatedCounts] = useState<RelatedCounts | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchStudents = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: profiles, error: profilesError }, { data: studentRows, error: studentsError }, { data: groups, error: groupsError }] =
        await Promise.all([
          supabase.from('zehrahoca_profiles').select('id, full_name, email, phone, active').eq('role', 'ogrenci').order('created_at', { ascending: true }),
          supabase.from('zehrahoca_students').select('*'),
          supabase.from('zehrahoca_groups').select('id, name'),
        ]);

      if (profilesError) throw profilesError;
      if (studentsError) throw studentsError;
      if (groupsError) throw groupsError;

      const groupMap: Record<string, string> = {};
      (groups ?? []).forEach((g: { id: string; name: string }) => {
        groupMap[g.id] = g.name;
      });

      const studentMap: Record<string, typeof studentRows extends (infer T)[] | null ? T : never> = {};
      (studentRows ?? []).forEach((s: Record<string, unknown>) => {
        studentMap[s.id as string] = s as never;
      });

      const merged: StudentRow[] = (profiles ?? []).map((p: Record<string, unknown>) => {
        const s = studentMap[p.id as string] as Record<string, unknown> | undefined;
        return {
          id: p.id as string,
          full_name: (p.full_name as string) ?? '',
          email: (p.email as string) ?? null,
          phone: (p.phone as string) ?? null,
          active: (p.active as boolean) ?? true,
          level: (s?.level as string) ?? null,
          group_id: (s?.group_id as string) ?? null,
          parent_name: (s?.parent_name as string) ?? null,
          parent_phone: (s?.parent_phone as string) ?? null,
          register_date: (s?.register_date as string) ?? null,
          last_login: (s?.last_login as string) ?? null,
          performance: (s?.performance as string) ?? null,
          attendance: (s?.attendance as string) ?? null,
          notes: (s?.notes as string) ?? null,
          group_name: s?.group_id ? (groupMap[s.group_id as string] ?? null) : null,
        };
      });

      setStudents(merged);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    const handleChange = () => fetchStudents();
    window.addEventListener('zehrahoca:students-changed', handleChange);
    window.addEventListener('zehrahoca:groups-changed', handleChange);
    return () => {
      window.removeEventListener('zehrahoca:students-changed', handleChange);
      window.removeEventListener('zehrahoca:groups-changed', handleChange);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      const matchesSearch =
        !q ||
        `${s.full_name} ${s.email ?? ''} ${s.phone ?? ''} ${s.level ?? ''} ${s.group_name ?? ''}`.toLowerCase().includes(q);
      const matchesFilter =
        activeFilter === 'tumu' || (activeFilter === 'aktif' ? s.active : !s.active);
      return matchesSearch && matchesFilter;
    });
  }, [students, search, activeFilter]);

  const activeCount = students.filter((s) => s.active).length;
  const passiveCount = students.filter((s) => !s.active).length;

  function handleNew() {
    setEditStudent(null);
    setModalOpen(true);
  }

  function handleEdit(s: StudentRow) {
    setEditStudent(s);
    setModalOpen(true);
  }

  function handleDetail(s: StudentRow) {
    setDetailStudent(s);
  }

  function openToggle(s: StudentRow, action: ToggleAction) {
    setDeleteError(null);
    setConfirmToggle({ student: s, action });
  }

  async function applyToggle(student: StudentRow, action: ToggleAction) {
    setToggling(true);
    setDeleteError(null);
    try {
      const newActive = action === 'activate';
      const { error: updateError } = await supabase
        .from('zehrahoca_profiles')
        .update({ active: newActive })
        .eq('id', student.id);
      if (updateError) throw updateError;
      setConfirmToggle(null);
      setDeleteStudent(null);
      fetchStudents();
      setSuccess(newActive ? 'Öğrenci tekrar aktif edildi.' : 'Öğrenci pasife alındı.');
    } catch (err) {
      setDeleteError((err as Error).message || 'Durum güncellenirken bir hata oluştu.');
      setConfirmToggle(null);
      setDeleteStudent(null);
    } finally {
      setToggling(false);
    }
  }

  async function openDelete(s: StudentRow) {
    setDeleteStudent(s);
    setRelatedCounts(null);
    setCheckingRelated(true);
    setDeleteError(null);
    try {
      const [packages, payments, submissions] = await Promise.all([
        supabase.from('zehrahoca_packages').select('id', { count: 'exact', head: true }).eq('student_id', s.id),
        supabase.from('zehrahoca_payments').select('id', { count: 'exact', head: true }).eq('student_id', s.id),
        supabase.from('zehrahoca_assignment_submissions').select('id', { count: 'exact', head: true }).eq('student_id', s.id),
      ]);
      setRelatedCounts({
        packages: packages.count ?? 0,
        payments: payments.count ?? 0,
        submissions: submissions.count ?? 0,
      });
    } catch {
      setRelatedCounts({ packages: 0, payments: 0, submissions: 0 });
    } finally {
      setCheckingRelated(false);
    }
  }

  async function doDelete() {
    if (!deleteStudent || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const studentId = deleteStudent.id;

      const { data, error: invokeError } = await supabase.functions.invoke('zehrahoca-delete-student', {
        body: { student_id: studentId, force: true },
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Sunucuyla iletişim kurulamadı. Lütfen tekrar deneyin.');
      }
      if (!data) {
        throw new Error('Sunucudan geçersiz yanıt alındı. Lütfen tekrar deneyin.');
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      setDeleteStudent(null);
      setRelatedCounts(null);
      fetchStudents();
      window.dispatchEvent(new Event('zehrahoca:groups-changed'));
      setSuccess(typeof data?.message === 'string' ? data.message : 'Öğrenci kalıcı olarak silindi.');
    } catch (err) {
      setDeleteError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setDeleteStudent(null);
      setRelatedCounts(null);
    } finally {
      setDeleting(false);
    }
  }

  const toEdit = (s: StudentRow): StudentEdit => ({
    id: s.id,
    full_name: s.full_name,
    email: s.email,
    phone: s.phone,
    active: s.active,
    level: s.level,
    group_id: s.group_id,
    parent_name: s.parent_name,
    parent_phone: s.parent_phone,
    notes: s.notes,
  });

  const toDetail = (s: StudentRow): StudentDetail => ({
    id: s.id,
    full_name: s.full_name,
    email: s.email,
    phone: s.phone,
    active: s.active,
    level: s.level,
    group_id: s.group_id,
    parent_name: s.parent_name,
    parent_phone: s.parent_phone,
    register_date: s.register_date,
    notes: s.notes,
    group_name: s.group_name,
  });

  const hasRelated = !!relatedCounts && (relatedCounts.packages + relatedCounts.payments + relatedCounts.submissions) > 0;

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Öğrenciler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Öğrenciler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={() => fetchStudents()}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Öğrenciler</h1>
          <p className="mt-1 text-sm text-foreground-600">
            {students.length} öğrenci · {activeCount} aktif · {passiveCount} pasif
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-user-add-line text-base" />
          Yeni Öğrenci Ekle
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full max-w-md">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, e-posta, telefon veya seviye ara..."
            className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-10 pr-3.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {([
            { key: 'tumu', label: 'Tümü' },
            { key: 'aktif', label: 'Aktif' },
            { key: 'pasif', label: 'Pasif' },
          ] as { key: ActiveFilter; label: string }[]).map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setActiveFilter(f.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
                activeFilter === f.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {deleteError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base" />
            <span>{deleteError}</span>
          </div>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="text-red-500 transition hover:text-red-700 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <div className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-base" />
            <span>{success}</span>
          </div>
          <button
            type="button"
            onClick={() => setSuccess(null)}
            className="text-emerald-500 transition hover:text-emerald-700 cursor-pointer"
            aria-label="Kapat"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-background-300 bg-white p-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-user-star-line text-2xl" />
          </div>
          <p className="mt-4 text-sm font-semibold text-foreground-800">
            {students.length === 0 ? 'Henüz öğrenci eklenmemiş' : 'Filtre veya aramanızla eşleşen öğrenci bulunamadı'}
          </p>
          <p className="mt-1 text-sm text-foreground-500">
            {students.length === 0 ? 'İlk öğrencinizi ekleyerek başlayın.' : 'Farklı bir filtre veya arama terimi deneyin.'}
          </p>
          {students.length === 0 && (
            <button
              type="button"
              onClick={handleNew}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base" /> Öğrenci Ekle
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((s) => {
            const initial = (s.full_name || '?').trim().charAt(0).toUpperCase();
            return (
              <div key={s.id} className={`rounded-xl border bg-white p-5 transition hover:border-background-300 ${s.active ? 'border-background-200' : 'border-background-200 opacity-90'}`}>
                <div className="flex items-start gap-3.5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-700">
                    {initial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-base font-bold text-foreground-950">{s.full_name}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      {s.level && (
                        <span className="inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
                          {s.level}
                        </span>
                      )}
                      {s.group_name && (
                        <span className="inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800">
                          {s.group_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      s.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'
                    }`}
                  >
                    <i className={s.active ? 'ri-checkbox-circle-fill' : 'ri-pause-circle-line'} />
                    {s.active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>

                <div className="mt-4 space-y-1.5 text-sm">
                  {s.email && (
                    <div className="flex items-center gap-2 text-foreground-600">
                      <i className="ri-mail-line w-4 text-foreground-400" />
                      <span className="truncate">{s.email}</span>
                    </div>
                  )}
                  {s.phone && (
                    <div className="flex items-center gap-2 text-foreground-600">
                      <i className="ri-phone-line w-4 text-foreground-400" />
                      <span>{s.phone}</span>
                    </div>
                  )}
                  {s.parent_name && (
                    <div className="flex items-center gap-2 text-foreground-600">
                      <i className="ri-user-heart-line w-4 text-foreground-400" />
                      <span className="truncate">Veli: {s.parent_name}{s.parent_phone ? ` · ${s.parent_phone}` : ''}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
                  <span className="text-xs text-foreground-500">
                    Kayıt: {s.register_date ? new Date(s.register_date).toLocaleDateString('tr-TR') : '—'}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleDetail(s)}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-background-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-eye-line" /> Detay
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEdit(s)}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-background-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-edit-line" /> Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => openToggle(s, s.active ? 'deactivate' : 'activate')}
                    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-semibold transition cursor-pointer ${
                      s.active ? 'bg-background-100 text-foreground-600 hover:bg-background-200' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    }`}
                  >
                    <i className={s.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                    {s.active ? 'Pasife Al' : 'Aktif Et'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openDelete(s)}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" /> Kalıcı Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StudentModal open={modalOpen} onClose={() => setModalOpen(false)} student={editStudent ? toEdit(editStudent) : null} />

      <StudentDetailModal student={detailStudent ? toDetail(detailStudent) : null} onClose={() => setDetailStudent(null)} />

      {/* Pasife Al / Aktif Et onay modalı */}
      <Modal
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={confirmToggle?.action === 'deactivate' ? 'Öğrenciyi pasife almak istiyor musunuz?' : 'Öğrenciyi tekrar aktif etmek istiyor musunuz?'}
        subtitle={confirmToggle?.student.full_name}
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            {confirmToggle?.action === 'deactivate'
              ? 'Bu işlem öğrenciyi silmez. Öğrencinin ödeme, paket, ders ve ödev geçmişi korunur. Öğrenci aktif listelerde pasif olarak görünür.'
              : 'Öğrenci tekrar aktif hale gelir ve bağlı olduğu aktif grubun içeriklerine erişebilir.'}
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmToggle(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={() => confirmToggle && applyToggle(confirmToggle.student, confirmToggle.action)}
              disabled={toggling}
              className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition disabled:opacity-70 cursor-pointer whitespace-nowrap ${
                confirmToggle?.action === 'deactivate' ? 'bg-secondary-500 hover:bg-secondary-600' : 'bg-emerald-500 hover:bg-emerald-600'
              }`}
            >
              {toggling ? 'İşleniyor...' : confirmToggle?.action === 'deactivate' ? 'Pasife Al' : 'Aktif Et'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Kalıcı Sil modalı (ön kontrol ile) */}
      <Modal
        open={!!deleteStudent}
        onClose={() => setDeleteStudent(null)}
        title={checkingRelated ? 'Geçmiş kayıtlar kontrol ediliyor...' : hasRelated ? 'Bu öğrencinin geçmiş kayıtları var' : 'Öğrenciyi kalıcı olarak sil'}
        subtitle={deleteStudent?.full_name}
      >
        <div className="space-y-5">
          {checkingRelated ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
            </div>
          ) : hasRelated ? (
            <>
              <p className="text-sm text-foreground-700">
                Bu öğrencinin ödeme, paket veya ödev teslim geçmişi bulunuyor. Kalıcı silme işlemi bu geçmiş kayıtları etkileyebilir. Günlük kullanım için öğrenciyi pasife almanız önerilir.
              </p>
              {relatedCounts && (
                <div className="rounded-lg bg-background-50 p-3.5 text-sm text-foreground-600">
                  <p className="text-xs font-semibold text-foreground-500">Tespit edilen geçmiş kayıtlar:</p>
                  <ul className="mt-2 space-y-1 text-xs">
                    <li className="flex items-center gap-2"><i className="ri-wallet-3-line text-foreground-400" /> {relatedCounts.packages} paket</li>
                    <li className="flex items-center gap-2"><i className="ri-money-dollar-circle-line text-foreground-400" /> {relatedCounts.payments} ödeme</li>
                    <li className="flex items-center gap-2"><i className="ri-task-line text-foreground-400" /> {relatedCounts.submissions} ödev teslimi</li>
                  </ul>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-background-200 pt-5">
                <button
                  type="button"
                  onClick={() => setDeleteStudent(null)}
                  className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={() => deleteStudent && applyToggle(deleteStudent, 'deactivate')}
                  disabled={toggling || deleting}
                  className="rounded-lg bg-secondary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-secondary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
                >
                  Pasife Al
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={deleting || toggling}
                  className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
                >
                  {deleting ? 'Siliniyor...' : 'Yine de Kalıcı Sil'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-foreground-700">
                <span className="font-semibold">{deleteStudent?.full_name}</span> adlı öğrenciyi kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
              </p>
              <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
                <button
                  type="button"
                  onClick={() => setDeleteStudent(null)}
                  className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={deleting}
                  className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
                >
                  {deleting ? 'Siliniyor...' : 'Kalıcı Sil'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}